<template>
  <div id="subscription" class="fill-height align-start">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      :text="loadingMessage"
    />

    <template v-if="!loading && subscriptionPlans">
      <v-row :class="$route.meta.class" no-gutters>
        <v-col cols="12">
          <h2 class="ma-0 text-center">{{ $route.meta.title }}</h2>

          <v-row>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="account/subscription"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col v-if="hasSubscription" cols="12">
              <v-simple-table
                class="subscription-table mt-4 mt-md-6 elevation-1"
                rounded
              >
                <template v-slot:default>
                  <thead v-if="$vuetifyDisplay.smAndDown">
                    <tr>
                      <th class="features-column py-4">
                        <h3 class="pb-2">Current Subscription</h3>
                        <h3
                          class="subscription-level py-2 mb-3"
                          :class="{
                            'subscription-level-gold':
                              subscriptionStatus === 'GOLD',
                            'subscription-level-silver':
                              subscriptionStatus === 'SILVER',
                            'subscription-level-review':
                              subscriptionStatus === 'REVIEW_SUBSCRIPTION'
                          }"
                        >
                          {{ subscriptionStatusDisplayName }}
                        </h3>
                        <v-btn
                          rounded
                          class="i-btn-block-secondary"
                          width="250"
                          @click="manageExistingSubscription()"
                        >
                          Manage Subscription
                        </v-btn>
                      </th>
                    </tr>
                  </thead>

                  <thead v-else>
                    <tr>
                      <th class="features-column py-4">
                        <h3 class="pb-0">Current Subscription</h3>
                      </th>
                      <th class="plan-column py-4">
                        <h3
                          class="subscription-level pb-0"
                          :class="{
                            'subscription-level-gold':
                              subscriptionStatus === 'GOLD',
                            'subscription-level-silver':
                              subscriptionStatus === 'SILVER',
                            'subscription-level-review':
                              subscriptionStatus === 'REVIEW_SUBSCRIPTION'
                          }"
                        >
                          {{ subscriptionStatusDisplayName }}
                        </h3>
                      </th>
                      <th class="plan-column py-4">
                        <v-btn
                          rounded
                          class="i-btn-block-secondary"
                          @click="manageExistingSubscription()"
                        >
                          Manage Subscription
                        </v-btn>
                      </th>
                    </tr>
                  </thead>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>

          <v-row class="mt-6 mb-8" no-gutters>
            <v-col cols="12">
              <template v-if="$vuetifyDisplay.smAndDown">
                <v-row class="mb-6">
                  <v-col cols="6" class="text-right">
                    <v-btn
                      large
                      class="i-btn-select g-full-width"
                      :class="{ 'i-btn-select-active': !basic }"
                      @click="selectPremium()"
                    >
                      {{ premiumPlanDetails.name }}
                    </v-btn>
                  </v-col>
                  <v-col cols="6" class="text-left">
                    <v-btn
                      large
                      class="i-btn-select g-full-width"
                      :class="{ 'i-btn-select-active': basic }"
                      @click="selectBasic()"
                    >
                      {{ basicPlanDetails.name }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-simple-table
                  v-if="!basic"
                  class="subscription-table mt-2 elevation-1"
                  rounded
                >
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="features-column">
                          <h3>Supplier Subscriptions</h3>
                        </th>
                        <th class="plan-column premium py-6">
                          <h3 class="pb-0">{{ premiumPlanDetails.name }}</h3>
                          <p>{{ premiumPlanDetails.description }}</p>
                          <v-btn
                            v-if="!hasSubscription"
                            rounded
                            class="i-btn-block-secondary"
                            @click="payPremium()"
                            >Pay
                          </v-btn>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-show="selectedPlan">
                        <td colspan="3">
                          <v-card flat class="pa-4 card-entry">
                            <i-stripe-card-payment
                              :plan="selectedPlan"
                              ref="stripeCardPayment"
                            ></i-stripe-card-payment>
                          </v-card>
                        </td>
                      </tr>
                      <tr>
                        <td class="subtitle" colspan="3"></td>
                      </tr>
                      <tr>
                        <td class="py-4">Price</td>
                        <td class="py-4">
                          <span class="price">{{
                            $filters.currency(
                              premiumPlanDetails.amountInCents / 100
                            )
                          }}</span>
                          / month
                        </td>
                      </tr>
                      <tr>
                        <td class="subtitle">Data Setup & Maintenance</td>
                        <td class="subtitle"></td>
                      </tr>
                      <tr>
                        <td>Product Hosting</td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td>Bulk Upload capability</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Pricing (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Ranging (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>
                          Order Minimum & Free Freight / Additional Freight Cost
                          per Carton
                        </td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Pricebook / Promotions download</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Rebates (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Keywords per Product</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Supplier and Brand Logos</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle">Promotions</td>
                        <td class="subtitle"></td>
                      </tr>
                      <tr>
                        <td>Family Groups</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>
                          Specials & Price Drops (Geography / Banner / MSO /
                          Retailer)
                        </td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td>
                          Volume Buys (Geography / Banner / MSO / Retailer)
                        </td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Coupons</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle">Order Management / Reporting</td>
                        <td class="subtitle"></td>
                      </tr>
                      <tr>
                        <td>Purchase Order Management</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Daily Credit Returns</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Order Queries</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Reporting and Data suite</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Retailer Turn In Orders</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Multiple Account User functionality</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle" colspan="3"></td>
                      </tr>
                      <tr>
                        <td></td>
                        <td class="plan-column premium py-6">
                          <h3 class="pb-0">{{ premiumPlanDetails.name }}</h3>
                          <p>{{ premiumPlanDetails.description }}</p>
                          <v-btn
                            v-if="!hasSubscription"
                            rounded
                            class="i-btn-block-secondary"
                            @click="payPremium(true)"
                            >Pay
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <v-simple-table
                  v-if="basic"
                  class="subscription-table mt-2 elevation-1"
                  rounded
                >
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="features-column">
                          <h3>Supplier Subscriptions</h3>
                        </th>
                        <th class="plan-column basic py-6">
                          <h3 class="pb-0">{{ basicPlanDetails.name }}</h3>
                          <p>{{ basicPlanDetails.description }}</p>
                          <v-btn
                            v-if="!hasSubscription"
                            rounded
                            class="i-btn-block-secondary"
                            @click="payBasic()"
                            >Pay
                          </v-btn>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-show="selectedPlan">
                        <td colspan="3">
                          <v-card flat class="pa-4 card-entry">
                            <i-stripe-card-payment
                              :plan="selectedPlan"
                              ref="stripeCardPayment"
                            ></i-stripe-card-payment>
                          </v-card>
                        </td>
                      </tr>
                      <tr>
                        <td class="subtitle" colspan="2"></td>
                      </tr>
                      <tr>
                        <td class="py-4">Price</td>
                        <td class="py-4">
                          <span class="price">{{
                            $filters.currency(
                              basicPlanDetails.amountInCents / 100
                            )
                          }}</span>
                          / month
                        </td>
                      </tr>
                      <tr>
                        <td class="subtitle">Data Setup & Maintenance</td>
                        <td class="subtitle"></td>
                      </tr>
                      <tr>
                        <td>Product Hosting</td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td>Bulk Upload capability</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Pricing (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Ranging (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>
                          Order Minimum & Free Freight / Additional Freight Cost
                          per Carton
                        </td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Pricebook / Promotions download</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Rebates (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Keywords per Product</td>
                        <td><i class="check mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td>Supplier and Brand Logos</td>
                        <td><i class="check mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle">Promotions</td>
                        <td class="subtitle"></td>
                      </tr>
                      <tr>
                        <td>Family Groups</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>
                          Specials & Price Drops (Geography / Banner / MSO /
                          Retailer)
                        </td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td>
                          Volume Buys (Geography / Banner / MSO / Retailer)
                        </td>
                        <td><i class="check mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td>Coupons</td>
                        <td><i class="check mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle">Order Management / Reporting</td>
                        <td class="subtitle"></td>
                      </tr>
                      <tr>
                        <td>Purchase Order Management</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Daily Credit Returns</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Order Queries</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Reporting and Data suite</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Retailer Turn In Orders</td>
                        <td><i class="check mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td>Multiple Account User functionality</td>
                        <td><i class="check mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle" colspan="3"></td>
                      </tr>
                      <tr>
                        <td></td>
                        <td class="plan-column basic py-6">
                          <h3 class="pb-0">{{ basicPlanDetails.name }}</h3>
                          <p>{{ basicPlanDetails.description }}</p>
                          <v-btn
                            v-if="!hasSubscription"
                            rounded
                            class="i-btn-block-secondary"
                            @click="payBasic(true)"
                            >Pay
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </template>

              <template v-else>
                <v-simple-table
                  class="subscription-table mt-2 elevation-1"
                  rounded
                >
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="features-column">
                          <h3>Supplier Subscriptions</h3>
                        </th>
                        <th class="plan-column premium py-6">
                          <h3 class="pb-0">{{ premiumPlanDetails.name }}</h3>
                          <p>{{ premiumPlanDetails.description }}</p>
                          <v-btn
                            v-if="!hasSubscription"
                            rounded
                            class="i-btn-block-secondary mt-3"
                            @click="payPremium()"
                            >Select {{ premiumPlanDetails.name }} & Pay
                          </v-btn>
                        </th>
                        <th class="plan-column basic py-6">
                          <h3 class="pb-0">{{ basicPlanDetails.name }}</h3>
                          <p>{{ basicPlanDetails.description }}</p>
                          <v-btn
                            v-if="!hasSubscription"
                            rounded
                            class="i-btn-block-secondary mt-3"
                            @click="payBasic()"
                            >Select {{ basicPlanDetails.name }} & Pay
                          </v-btn>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-show="selectedPlan">
                        <td>&nbsp;</td>
                        <td colspan="2">
                          <v-card flat class="pa-4 card-entry">
                            <i-stripe-card-payment
                              :plan="selectedPlan"
                              ref="stripeCardPayment"
                            ></i-stripe-card-payment>
                          </v-card>
                        </td>
                      </tr>
                      <tr>
                        <td class="subtitle" colspan="3"></td>
                      </tr>
                      <tr>
                        <td class="py-8">Price</td>
                        <td>
                          <span class="py-8 price">{{
                            $filters.currency(
                              premiumPlanDetails.amountInCents / 100
                            )
                          }}</span>
                          / month
                        </td>
                        <td>
                          <span class="py-8 price">{{
                            $filters.currency(
                              basicPlanDetails.amountInCents / 100
                            )
                          }}</span>
                          / month
                        </td>
                      </tr>
                      <tr>
                        <td class="subtitle">Data Setup & Maintenance</td>
                        <td class="subtitle" colspan="2"></td>
                      </tr>
                      <tr>
                        <td>Product Hosting</td>
                        <td>Unlimited</td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td>Bulk Upload capability</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Pricing (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Ranging (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>
                          Order Minimum & Free Freight / Additional Freight Cost
                          per Carton
                        </td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Pricebook / Promotions download</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Rebates (Geography / Banner / MSO / Retailer)</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Keywords per Product</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="cross mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td>Supplier and Brand Logos</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="cross mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle">Promotions</td>
                        <td class="subtitle" colspan="2"></td>
                      </tr>
                      <tr>
                        <td>Family Groups</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>
                          Specials & Price Drops (Geography / Banner / MSO /
                          Retailer)
                        </td>
                        <td>Unlimited</td>
                        <td>Unlimited</td>
                      </tr>
                      <tr>
                        <td>
                          Volume Buys (Geography / Banner / MSO / Retailer)
                        </td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="cross mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td>Coupons</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="cross mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle">Order Management / Reporting</td>
                        <td class="subtitle" colspan="2"></td>
                      </tr>
                      <tr>
                        <td>Purchase Order Management</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Daily Credit Returns</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Order Queries</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Reporting and Data suite</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                      </tr>
                      <tr>
                        <td>Retailer Turn In Orders</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="cross mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td>Multiple Account User functionality</td>
                        <td><i class="check mdi mdi-check-bold"></i></td>
                        <td><i class="cross mdi mdi-minus"></i></td>
                      </tr>
                      <tr>
                        <td class="subtitle" colspan="3"></td>
                      </tr>
                      <tr>
                        <td></td>
                        <td class="plan-column premium py-6">
                          <h3 class="pb-0">{{ premiumPlanDetails.name }}</h3>
                          <p>{{ premiumPlanDetails.description }}</p>
                          <v-btn
                            v-if="!hasSubscription"
                            rounded
                            class="i-btn-block-secondary mt-3"
                            @click="payPremium(true)"
                            >Select {{ premiumPlanDetails.name }} & Pay
                          </v-btn>
                        </td>
                        <td class="plan-column basic py-6">
                          <h3 class="pb-0">{{ basicPlanDetails.name }}</h3>
                          <p>{{ basicPlanDetails.description }}</p>
                          <v-btn
                            v-if="!hasSubscription"
                            rounded
                            class="i-btn-block-secondary mt-3"
                            @click="payBasic(true)"
                            >Select {{ basicPlanDetails.name }} & Pay
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </template>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import iOverlay from '@/components/Overlay'
import iStripeCardPayment from '../components/StripeCardPayment'
import {
  fetchSubscriptionPlans,
  generateStripeSubscriptionPortalSession
} from '@/services/subscriptions'
import { mapGetters, mapState } from 'vuex'
import iInstructions from '@/components/Instructions.vue'

export default {
  components: {
    iInstructions,
    iOverlay,
    iStripeCardPayment
  },

  data() {
    return {
      subscriptionPlans: undefined,
      selectedPlan: undefined,
      premiumPlanDetails: undefined,
      basicPlanDetails: undefined,
      loading: true,
      loadingMessage: '',
      basic: false
    }
  },

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('profile', {
      hasSubscription: (state) => !!state.info?.subscription
    }),
    ...mapGetters('profile', [
      'subscriptionStatus',
      'subscriptionStatusDisplayName'
    ])
  },

  mounted() {
    this.fetchSubscriptionPlans()
  },

  methods: {
    fetchSubscriptionPlans() {
      this.loading = true
      this.loadingMessage = 'Fetching available subscriptions...'
      fetchSubscriptionPlans()
        .then((response) => {
          this.subscriptionPlans = response
          this.premiumPlanDetails = this.subscriptionPlans.find(
            (e) => e.level === 'GOLD'
          )
          this.basicPlanDetails = this.subscriptionPlans.find(
            (e) => e.level === 'SILVER'
          )

          return response
        })
        .finally(() => {
          this.loading = false
        })
    },

    manageExistingSubscription() {
      this.loading = true
      this.loadingMessage = 'Fetching subscription details...'
      generateStripeSubscriptionPortalSession(this.selectedSupplierId, {
        returnUrl: `${window.location.origin}/account`
      })
        .then((res) => {
          window.location.href = res.url
        })
        .catch(() => {
          this.loading = false
          this.addNotification({
            color: 'error',
            text: 'Unable to generate your Stripe subscription link. Please try again...'
          })
        })
    },

    payBasic(scrollToView) {
      this.selectBasic()
      this.selectedPlan = this.basicPlanDetails
      this.scrollStripePaymentIntoView(scrollToView)
    },

    payPremium(scrollToView) {
      this.selectPremium()
      this.selectedPlan = this.premiumPlanDetails
      this.scrollStripePaymentIntoView(scrollToView)
    },

    scrollStripePaymentIntoView: function (scrollToView) {
      const stripeCardPayment = this.$refs.stripeCardPayment
      if (stripeCardPayment) {
        stripeCardPayment.focus()
      }
      if (scrollToView) {
        this.$vuetify.goTo('#payment-form', { offset: 100 })
      }
    },

    selectBasic() {
      this.selectedPlan = undefined
      this.premium = false
      this.basic = true
    },

    selectPremium() {
      this.selectedPlan = undefined
      this.basic = false
      this.premium = true
    }
  }
}
</script>

<style lang="scss" scoped>
#subscription {
  .card-entry {
    .card-details--name {
      .v-icon {
        color: #d3d7e0;
      }
    }
  }

  .v-btn:not(.v-btn--text):not(.v-btn--outlined):hover:before {
    opacity: 0;
  }

  .subscription-level {
    background-color: $secondary-color;
    border-radius: 5px;
    color: white !important;
    margin: auto;
    width: 250px;

    &-gold {
      background-color: goldenrod !important;
    }

    &-silver {
      background-color: darkgray !important;
    }

    &-review {
      background-color: $red-color !important;
    }
  }

  .subscription-table {
    margin: auto;
    border-collapse: collapse;
    overflow: hidden;
    -moz-border-radius: 10px;
    -webkit-border-radius: 10px;
    border-radius: 10px;

    th {
      text-align: center;
      font-weight: bold;
      background: white;
    }

    td {
      text-align: center;
      background: white;
      font-size: 14px;
    }

    .amount {
      font-size: 48px;
      font-weight: bold;
    }

    .basic {
      border-bottom: 8px solid $secondary-color;
    }

    .check.mdi:before {
      font-size: 20px;
      color: $secondary-color;
    }

    .cross.mdi:before {
      font-size: 20px;
      color: gray;
    }

    .currency {
      position: relative;
      top: -25px;
    }

    .features-column h3 {
      text-transform: uppercase;
      color: black;
      font-size: 16px;
      font-weight: bold;
      letter-spacing: 1px;
    }

    .plan-column {
      width: 50%;
    }

    .plan-column h3 {
      font-size: 26px;
      color: black;
    }

    .plan-column p {
      font-size: 16px;
      font-weight: normal;
    }

    .price {
      font-size: 28px;
      font-weight: bold;
    }

    .premium {
      border-bottom: 8px solid $link-color;
    }

    .subtitle {
      text-transform: uppercase;
      font-size: 14px;
      font-weight: bold;
      letter-spacing: 1px;
      background: #f2f2f2;
    }
  }
}

@media only screen and (min-width: 960px) {
  #subscription {
    .subscription-level {
      width: 85%;
    }

    .subscription-table {
      td {
        font-size: 16px;
      }

      th {
        font-size: 16px;
      }

      .plan-column {
        width: 33%;
      }
    }
  }
}

@media only screen and (min-width: 1240px) {
  #subscription {
    .subscription {
      &-table,
      &-management {
        max-width: 85%;
        margin: auto;
      }
    }
  }
}
</style>
